// Copyright (c) 2022-2023 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import {
  RouteComponentProps,
  Switch,
  Route,
  Redirect,
  RouteProps,
} from 'react-router-dom';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable, toJS } from 'mobx';

import NavigationMenu, { isMenuOpen, Item } from './components/NavigationMenu';
import Header, { HeaderConfig } from './components/Header';
import NoMatch from '@shared/components/NoMatch';
import Lazyload from '@shared/components/LazyLoad';
import ROUTES, { ACCESS } from '@shared/constants/routes';
import MENU_ITEMS from '@modules/Private/constants/menu';
import AuthStore from '@core/stores/auth';
import container from '@core/di';
import { ROLES } from '@shared/constants/user';
import { MergeTypes } from '@shared/types/common';
import { ManageMode } from '@shared/components/Form';
import ErrorHandler from '@shared/components/ErrorHandler';
import Timer from './components/Timer';

// Set up to load the bundle contain a component when it is first rendered. 
// WXYZ: 'not found' <component_names are here.
const Staff = React.lazy(() => import('./submodules/Staff'));  // to load the old Staff component.
//const Staff = React.lazy(() => import('./submodules/StaffGrid')); // to load the new StaffGrid component.
const Profile = React.lazy(() => import('./submodules/Profile'));
const Orders = React.lazy(() => import('./submodules/Orders'));
const Customers = React.lazy(() => import('./submodules/Customers'));
const Assets = React.lazy(() => import('./submodules/Assets'));
const Platforms = React.lazy(() => import('./submodules/Platforms'));
const JobRequests = React.lazy(() => import('./submodules/JobRequests'));
const Vendors = React.lazy(() => import('./submodules/Vendors'));
const Dashboard = React.lazy(() => import('./submodules/Dashboard'));
const DataManagement = React.lazy(() => import('./submodules/DataManagement'));
const Clusters = React.lazy(() => import('./submodules/Clusters'));
const Map = React.lazy(() => import('./submodules/Map'));
const SPContainer = React.lazy(() => import('./submodules/SPContainer'));
const AlarmContainer = React.lazy(() => import('./submodules/AlarmContainer'));
const Template = React.lazy(() => import('./submodules/Template')); // Template for testing/adding new pages.

import { PublicClientApplication } from '@azure/msal-browser';
import { NotificationType, showNotification } from '@shared/components/Notification';
import { errObj2Str } from '@shared/utils/common';
import { TOKENS_KEYS } from '@shared/constants/common';
import Measurement from './submodules/Measurement/Measurement';
import WindArea from './submodules/WindArea';

export interface PrivateModuleProps extends RouteComponentProps {
  classes: any;       // to suppress lint warnings.
  className?: string;
}

// Default landing page for different roles.
export const getDefaultRoute = (): string => {
  const authStore = container.get<AuthStore>(AuthStore.diToken);
  const paths = {
    [ROLES.observer]: ROUTES.private.sheets,
    [ROLES.pilot]: ROUTES.private.sheets,
    [ROLES.postProcessingTechnician]: ROUTES.private.sheets,
    [ROLES.productionTechnician]: ROUTES.private.sheets,
    [ROLES.customerUser]: ROUTES.private.orders,
    [ROLES.projectManager]: ROUTES.private.sheets,
    [ROLES.customerUser]: ROUTES.private.sheets,
    [ROLES.contextCaptureClusterAgent]: ROUTES.private.sheets,
    [ROLES.techSupport]: ROUTES.private.sheets,
  };
  const { roles } = authStore.user;
  const defaultRole = roles[0];

  return roles.includes(ROLES.projectManager)
    ? paths[ROLES.projectManager]
    : paths[defaultRole];
};

const PUBLIC_ROUTES = Object.values(ROUTES.public);
const PRIVATE_ROUTES = Object.values(ROUTES.private);

export const RefreshFlagContext = React.createContext( {
  refreshFlag: false,
  setRefresh: (flag: boolean) => {console.log("********** Handle setRefresh with flag:", flag)} // Doesn't seem to do anything here.
});

// Component to handle the private branch of the site.
@observer
class Private extends React.Component<PrivateModuleProps> {
  private authStore = container.get<AuthStore>(AuthStore.diToken);
  @observable appCrashed: boolean = false;
  @observable _isMenuOpen = isMenuOpen();
  @observable headerConfig: HeaderConfig = {
    moduleName: ''
  };

  constructor(props: PrivateModuleProps) {
    super(props);
    makeObservable(this);
  }

  setRefresh = (flag: boolean) => {
    this.setState({ refreshFlag: flag });
  };

  state = {
    refreshFlag: false,
    setRefresh: this.setRefresh
  };

  @action componentDidCatch() {
    this.appCrashed = true;
  }

  // Return main endpoints of the private branch.
  private get routes(): Array<{
    roles: Array<string>;
    routeProps: MergeTypes<{ component: React.ComponentType<any> }, Omit<RouteProps, 'component'>>}> {
    const submodules = [
      {
        routeProps: { path: ROUTES.private.staff, component: Staff },
        roles: ACCESS.staff,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.customers, component: Customers },
        roles: ACCESS.customers,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.assets, component: Assets },
        roles: ACCESS.assets,
        onlyForHosting: true
      },
      {
        routeProps: { path: ROUTES.private.profile, component: Profile },
        roles: ACCESS.profile,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.alarms, component: AlarmContainer },
        roles: ACCESS.profile,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.orders, component: Orders },
        roles: ACCESS.orders,
        onlyForHosting: true
      },
      {
        routeProps: { path: ROUTES.private.platforms, component: Platforms },
        roles: ACCESS.platforms,
        onlyForHosting: true
      },
      {
        routeProps: { path: ROUTES.private.jobRequests, component: JobRequests },
        roles: ACCESS.jobRequests,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.vendors, component: Vendors },
        roles: ACCESS.vendors,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.dashboard, component: Dashboard },
        roles: ACCESS.dashboard,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.dataManagement, component: DataManagement },
        roles: ACCESS.dataManagement,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.clusters, component: Clusters },
        roles: ACCESS.clusters,
        onlyForHosting: true
      },
      {
        routeProps: { path: ROUTES.private.map, component: Map },
        roles: ACCESS.map,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.sheets, component: SPContainer },
        roles: ACCESS.sheets,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.measure, component: Measurement },
        roles: ACCESS.sheets,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.windarea, component: WindArea },
        roles: ACCESS.sheets,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.alarms, component: AlarmContainer },
        roles: ACCESS.alarms,
        onlyForHosting: false
      },
      {
        routeProps: { path: ROUTES.private.template, component: Template },
        roles: ACCESS.template,
        onlyForHosting: false
      }
    ];

    // Return endpoints available for a given set of roles.
    const ret = submodules.filter((module) => 
      (!module.onlyForHosting || this.authStore.user.isHostingCompany) && 
      module.roles.some((moduleRole) => this.authStore.user.roles.includes(moduleRole))
    );

    return ret;
  }

  // 
  private get modules() {
    const { location } = this.props;
    const redirectPathname = this.authStore.loginRedirectURL || getDefaultRoute();

    return (
      <Lazyload>
        <Switch>
          {this.routes.map(({ routeProps: { component: $component, ...otherRouteProps } }) => (
            <Route
              key={String(otherRouteProps.path)}
              render={(props) => <$component {...props} setHeaderConfig={this.setHeaderConfig} />}
              {...otherRouteProps}
            />
          ))}

          <Redirect exact from={ROUTES.initial} to={redirectPathname} />

          {PUBLIC_ROUTES.map((route) => <Redirect exact key={route} from={route} to={redirectPathname} />)}
          <Route
            render={() => {
              const rootRoute = (location.pathname.match(/^\/[^/]+/) || [])[0];

              return <NoMatch forbidden={PRIVATE_ROUTES.some((route) => rootRoute === route)} />;
            }}
          />
        </Switch>
      </Lazyload>
    );
  }

  //
  private get menuItems(): Item[] {
    return MENU_ITEMS.filter((item) => (!item.onlyForHosting || this.authStore.user.isHostingCompany) && 
                                        item.roles.some((itemRole) => this.authStore.user.roles.includes(itemRole)));
  }

  //
  @action setHeaderConfig = (config: HeaderConfig) => {
    this.headerConfig = config;
  }

  @computed get getHeaderConfig() {
    return this.headerConfig;
  }

  //
  private setAddButtonState = (nextButtonState: boolean) => {
    const currentConfig = toJS(this.getHeaderConfig);
    const currentButtonState = currentConfig.addButton?.disabled;

    if (currentButtonState !== nextButtonState) {
      this.setHeaderConfig({
        textField: undefined,
        ...currentConfig,
        addButton: {
          ...currentConfig.addButton,
          disabled: nextButtonState,
        }
      });
    }
  }

  //
  componentDidUpdate() {
    const allManageModes = [ManageMode.create, ManageMode.edit];
    const { pathname, search } = this.props.location;
    const url = `${pathname}${search}`;
    const isActiveItemManagement = allManageModes.some((type) => url.includes(type));

    this.setAddButtonState(isActiveItemManagement);
  }

  @action handleToggleMenu = (isOpen: boolean) => {
    this._isMenuOpen = isOpen;
  }

  @computed get isMenuOpen() {
    return this._isMenuOpen;
  }

  async handleLogout() {
    console.log("*** HandleLogout");

    console.log("*** Do normal logout.");
    this.authStore.logout(true);

    // Read data from session storage.
    const tokenMs = sessionStorage.getItem(TOKENS_KEYS.msLogin);
    const tokenGoogle = sessionStorage.getItem(TOKENS_KEYS.googleLogin);

    console.log("*** tokens:", tokenGoogle, tokenMs);

    // Clear tokens stored in browser.
    sessionStorage.setItem(TOKENS_KEYS.msLogin, '');
    sessionStorage.setItem(TOKENS_KEYS.googleLogin, '');
    sessionStorage.setItem(TOKENS_KEYS.itwinsLogin, '');

    if (tokenMs) {
      console.log("*** Do MS logout:", tokenMs);
      // TBD: Read from conf file.
      const msalConfig = {
        auth: {
          clientId: 'f7ea623c-9fc5-4b58-a2f4-13d6945fcb2d', // Azure AD application's client ID
          //authority: 'https://login.microsoftonline.com/bfc9d3a4-5952-439d-b295-cad951515585', // application's tenant GUID 
          redirectUri: 'http://localhost/api/v1/auth/google', // ???
        }
      };

      try {
        const msalInstance = new PublicClientApplication(msalConfig);

        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();

        console.log("*** CHECK call logoutRedirect.");
        await msalInstance.logoutRedirect() // Aparently, this call redirect to the current page, not "redirectUri".  
        // So this page flow ends here.  TBD: maybe want to set up a callback to do cleanups and skip redirection.
      }
      catch (err) {
        console.log("*** error in handleMsLogin:", err);
        
        showNotification(errObj2Str(err), NotificationType.error);
      }    
    }

    if(tokenGoogle) {
      console.log("*** Do Google logout:", tokenGoogle);
    }

  }

  // Show the main page: NavBar, Header, and the routed modules.
  // There is also a timer for refresh/autologout.
  render() {
    const classes = withStyles.getClasses(this.props);
    const { user } = this.authStore;

    return (
      <div className={classes.root}>
        <NavigationMenu items={this.menuItems} onToggle={this.handleToggleMenu} />
        <div className={classes.content} style={{ width: this.isMenuOpen ? 'calc(100% - 200px)' : 'calc(100% - 48px)' }}>
          <Header
            {...this.getHeaderConfig}
            user={user}
            onLogoutClick={this.handleLogout.bind(this)}
          />

          <RefreshFlagContext.Provider value={this.state}>
            <Timer/>

            { /* Show current module */}
            <div className={classes.module}>
              {this.appCrashed ? <ErrorHandler /> : this.modules}
            </div>
          </RefreshFlagContext.Provider>
        </div>
      </div>
    );
  }
}

const PrivateStyled = withStyles(
  Private, 
  (theme, props) => ({
    "root": {
      height: '100%',
      display: 'flex',
    },
    "content": {
      position: 'relative',
      flexGrow: 2,
    },
    "module": {
      height: 'calc(100vh - 80px)',
      overflowX: 'hidden',
      overflowY: 'auto',
      position: 'relative',
      width: '100%',
    },
  })
);

export default PrivateStyled;